import { useMemo, useRef, useState, useEffect } from "react";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { COUNTRY_CODES } from "@/lib/countries";

/**
 * Compact country dial-code selector.
 * Value is the ISO code (e.g. "US"). onChange returns the full country object.
 */
export default function CountrySelect({ value = "US", onChange, testId = "country-select" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const selected = useMemo(
    () => COUNTRY_CODES.find((c) => c.code === value) || COUNTRY_CODES[0],
    [value],
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return COUNTRY_CODES;
    return COUNTRY_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.dial.includes(query) ||
        c.code.toLowerCase().includes(query),
    );
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        !popRef.current?.contains(e.target) &&
        !btnRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="h-11 pl-3 pr-2 rounded-l-xl border border-r-0 border-[#8A97A8]/30 bg-white flex items-center gap-1.5 text-navy-deep hover:border-cyan-brand transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-brand/40"
      >
        <span className="text-base leading-none" aria-hidden>
          {selected.flag}
        </span>
        <span className="font-mono-data text-[13px] tabular-nums">{selected.dial}</span>
        <CaretDown size={11} weight="bold" className="text-cool-grey" />
      </button>

      {open && (
        <div
          ref={popRef}
          role="listbox"
          data-testid={`${testId}-menu`}
          className="absolute z-50 mt-1.5 left-0 w-72 rounded-xl border border-[#8A97A8]/25 bg-white shadow-[0_20px_50px_-10px_rgba(2,47,107,0.25)] overflow-hidden"
        >
          <div className="p-2 border-b border-[#8A97A8]/15">
            <div className="relative">
              <MagnifyingGlass
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-cool-grey"
              />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                data-testid={`${testId}-search`}
                placeholder="Search country or code…"
                className="w-full h-9 pl-7 pr-2 rounded-lg text-[13px] bg-cool-white border border-[#8A97A8]/25 focus:outline-none focus:ring-2 focus:ring-cyan-brand/40 focus:border-cyan-brand"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-[12px] text-cool-grey">No match</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  data-testid={`${testId}-opt-${c.code}`}
                  onClick={() => {
                    onChange?.(c);
                    setOpen(false);
                    setQ("");
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] hover:bg-cool-white transition-colors ${
                    c.code === selected.code ? "bg-cyan-brand/5 text-navy-deep font-semibold" : "text-navy-deep"
                  }`}
                >
                  <span className="text-base" aria-hidden>
                    {c.flag}
                  </span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="font-mono-data text-cool-grey tabular-nums text-[12px]">
                    {c.dial}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
